import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyInfo } from '../mock';
import Header from '../components/Header';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy | SGS Global Media';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Privacy Policy for SGS Global Media. Learn how we collect, use, and protect your personal data.');
  }, []);

  const sections = [
    {
      title: '1. Information We Collect',
      content: `We collect information you provide directly to us when you:
• Fill out a contact or consultation form on our website
• Book a call via our scheduling system (Calendly)
• Send us an email or message

The types of information we may collect include: your name, email address, phone number, company name, and any other information you choose to provide.

We also automatically collect certain information when you visit our website, including your IP address, browser type, operating system, referring URLs, and pages viewed. This is collected via PostHog analytics.`,
    },
    {
      title: '2. How We Use Your Information',
      content: `We use the information we collect to:
• Respond to your enquiries and provide the services you request
• Send you information about our services, updates, and marketing communications (only where you have consented)
• Analyse website usage to improve our website and services
• Comply with legal obligations

We do not sell, rent, or share your personal information with third parties for their marketing purposes.`,
    },
    {
      title: '3. Legal Basis for Processing (GDPR)',
      content: `If you are located in the European Economic Area (EEA) or United Kingdom, our legal basis for collecting and using your personal information depends on the data concerned and the context in which we collect it:

• Contract: Processing is necessary to perform a contract with you or take steps at your request before entering into a contract
• Legitimate Interests: Processing is necessary for our legitimate interests (such as improving our services and communicating with prospects), provided those interests are not overridden by your rights
• Consent: Where you have given us consent to process your data for a specific purpose
• Legal Obligation: Where we are required to process your data to comply with a legal obligation`,
    },
    {
      title: '4. Data Retention',
      content: `We retain personal data for as long as necessary to fulfil the purposes for which it was collected, including for the purposes of satisfying any legal, accounting, or reporting requirements.

For enquiry and contact form data: we retain this for up to 2 years from the date of last contact.
For client data: we retain this for up to 7 years from the end of the engagement to comply with legal and financial record-keeping requirements.`,
    },
    {
      title: '5. Cookies and Tracking',
      content: `Our website uses cookies and similar tracking technologies to improve your browsing experience and analyse site traffic. We use PostHog analytics to understand how visitors interact with our website.

You can control cookie settings through your browser. Disabling cookies may affect some functionality of our website.

Types of cookies we use:
• Essential cookies: Required for the website to function properly
• Analytics cookies: Used to understand how visitors interact with our website (PostHog)`,
    },
    {
      title: '6. Data Transfers',
      content: `SGS Global Media is based in Pune, India. If you are located in the European Union, United Kingdom, or Australia, your data may be transferred to and processed in India.

We take appropriate safeguards to ensure that your personal information remains protected in accordance with this Privacy Policy when transferred internationally.`,
    },
    {
      title: '7. Your Rights',
      content: `Depending on your location, you may have the following rights regarding your personal data:

• Right of Access: Request a copy of the personal data we hold about you
• Right to Rectification: Request correction of inaccurate or incomplete data
• Right to Erasure: Request deletion of your personal data in certain circumstances
• Right to Restrict Processing: Request that we limit how we use your data
• Right to Data Portability: Request a machine-readable copy of your data
• Right to Object: Object to our processing of your data where we rely on legitimate interests
• Right to Withdraw Consent: Where processing is based on consent, withdraw that consent at any time

To exercise any of these rights, please contact us at ${companyInfo.email}.`,
    },
    {
      title: '8. Third-Party Services',
      content: `Our website uses the following third-party services that may collect and process data:

• PostHog: Analytics platform. Privacy Policy at posthog.com/privacy
• Calendly: Scheduling platform used for booking consultations. Privacy Policy at calendly.com/privacy
• Google Fonts: Font delivery service. Privacy Policy at policies.google.com/privacy

We are not responsible for the privacy practices of these third parties.`,
    },
    {
      title: '9. Children\'s Privacy',
      content: `Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us and we will promptly delete it.`,
    },
    {
      title: '10. Changes to This Policy',
      content: `We may update this Privacy Policy from time to time. We will notify you of significant changes by updating the date at the top of this page. We encourage you to review this policy periodically.`,
    },
    {
      title: '11. Contact Us',
      content: `If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:

SGS Global Media
Balewadi High Street, Pune, Maharashtra 411045, India
Email: ${companyInfo.email}

If you are located in the UK or EEA and are not satisfied with our response, you have the right to lodge a complaint with your local data protection authority.`,
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#020617', color: '#94a3b8', fontFamily: "'DM Sans', sans-serif" }}>

      <Header
        onBooking={() => { navigate('/'); setTimeout(() => { const el = document.querySelector('#contact'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 100); }}
        onSectionClick={(id) => { navigate('/'); setTimeout(() => { if (id && id !== 'home') { const el = document.querySelector(id); if (el) el.scrollIntoView({ behavior: 'smooth' }); } else { window.scrollTo({ top: 0, behavior: 'smooth' }); } }, 100); }}
      />

      {/* Hero */}
      <header className="pt-48 pb-16 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <nav aria-label="Breadcrumb" className="flex justify-center gap-2 text-xs text-gray-500 mb-6">
            <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }} className="hover:text-[#007bff] transition-colors">Home</a>
            <span>/</span>
            <span className="text-[#007bff]">Privacy Policy</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
            Privacy Policy
          </h1>
          <p className="text-gray-500 text-sm">Last updated: March 15, 2026</p>
        </div>
      </header>

      {/* Content */}
      <main className="pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div
            className="rounded-2xl p-8 md:p-12 mb-8"
            style={{ background: '#111620', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <p className="text-gray-400 leading-relaxed">
              SGS Global Media ("we", "us", or "our") is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your data when you visit <strong className="text-white">sgsglobalmedia.com</strong> or engage our services. Please read this policy carefully.
            </p>
          </div>

          <div className="space-y-6">
            {sections.map((section, i) => (
              <div
                key={i}
                className="rounded-2xl p-8 md:p-10"
                style={{ background: '#111620', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <h2 className="text-white font-bold text-lg mb-4" style={{ fontFamily: "'Syne', sans-serif" }}>
                  {section.title}
                </h2>
                <p className="text-gray-400 text-sm leading-[1.9] whitespace-pre-line">{section.content}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-10 px-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-3xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-sm font-extrabold" style={{ color: 'rgba(255,255,255,0.4)' }}>{companyInfo.name}</span>
          <nav aria-label="Legal links" className="flex gap-6">
            <a href="/privacy-policy" className="text-xs text-[#007bff] uppercase tracking-[0.15em]">Privacy Policy</a>
            <a href="/terms-of-service" onClick={e => { e.preventDefault(); navigate('/terms-of-service'); }} className="text-xs text-gray-600 hover:text-[#007bff] transition-colors uppercase tracking-[0.15em]">Terms of Service</a>
          </nav>
          <p className="text-gray-600 text-xs uppercase tracking-[0.2em]">© 2025 SGS Global Media</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
